import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';

import { Logger } from '../../logger/Logger';
import { isProdEnv } from '../../utils/env';
import { Curtain } from '../curtain/Curtain';

import type { ErrorBoundaryProps, ErrorBoundaryState } from './types';

/**
 * There's an error boundary component that provides a fallback in case of error in it's children components and logs error to Logger.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    Logger.logError('Core', 'error', Date.now(), 'ErrorBoundary', error.stack ?? error.message);
    Logger.logFailure('errors', 'errorBoundary', errorInfo?.componentStack ?? '', Date.now(), 1, error.message, false);
    Logger.logException(error);
    if (!isProdEnv()) {
      console.error('ErrorBoundary consumed an error:');
      console.error(error);
    }
    if (this.props.critical) {
      Curtain.resurrect();
    }
    this.setState({ hasError: true });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.critical) {
        return Curtain.renderContent(this.props.fallback) ?? null;
      } else {
        return this.props.fallback;
      }
    }
    return this.props.children;
  }
}
